import { FC, useMemo, ReactNode } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import { dateFormat } from 'constants/formats';

import { Encumbrance } from 'types';

import { RowCell } from 'modules/_shared/Table/types/RowCell';
import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import TableRow from 'modules/_shared/Table/components/TableRow';
import { isActiveEncumbrance } from 'modules/partners/utils/isActiveEncumbrance';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import {
  EncumbranceOption,
  encumbranceOptions,
} from 'modules/partners/constants/encumbranceOptions';

import CoOwnersTable from './CoOwnersTable';
import { useEncumbrancesTable } from '../context/EncumbrancesTableContext';

type EncumbrancesTableRowProps = {
  index: number;
  encumbrance: Encumbrance;
};

const EncumbrancesTableRow: FC<EncumbrancesTableRowProps> = ({
  index,
  encumbrance,
}) => {
  const { t } = useTranslate();

  const { data, actions } = useEncumbrancesTable();

  const encumbranceLabel = useMemo(() => {
    const selectedEncumbrance = encumbranceOptions.find(
      (option) => option.value === encumbrance?.type
    ) as EncumbranceOption | undefined;

    return selectedEncumbrance ? selectedEncumbrance.label : '';
  }, [encumbrance?.type]);

  const hasCoOwners = encumbrance?.coOwners && encumbrance.coOwners.length > 0;

  const handleExpandRow = actions.handleToggleRowExpansion
    ? () => actions.handleToggleRowExpansion!(index)
    : undefined;

  const handleViewEncumbrance = actions.handleViewEncumbrance
    ? () => actions.handleViewEncumbrance!(index)
    : undefined;

  const toggleOptions = [
    actions.handleViewEncumbrance && (
      <a
        className="d-flex cursor-pointer"
        key={`encumbrance-view-option-${index}`}
        onClick={() => actions.handleViewEncumbrance!(index)}
      >
        <em className="icon ni ni-eye fs-20px" />
        <span className="ml-1">{t('View')}</span>
      </a>
    ),
    actions.handleEditEncumbrance && (
      <a
        className="d-flex cursor-pointer"
        key={`encumbrance-edit-option-${index}`}
        onClick={() => actions.handleEditEncumbrance!(index)}
      >
        <em className="icon ni ni-edit fs-20px" />
        <span className="ml-1">{t('Edit')}</span>
      </a>
    ),
    actions.handleRemoveEncumbrance && (
      <a
        className="d-flex cursor-pointer"
        key={`encumbrance-remove-option-${index}`}
        onClick={() => actions.handleRemoveEncumbrance!(index)}
      >
        <em className="icon ni ni-trash fs-20px" />
        <span className="ml-1">{t('Delete')}</span>
      </a>
    ),
  ].filter(Boolean) as ReactNode[];

  const isActive = isActiveEncumbrance({ encumbrance });

  const hasCoOwnersColumn = Boolean(actions.handleToggleRowExpansion);

  const hasOptions =
    data.isAdmin && !data.isReadOnly && toggleOptions.length > 0;

  const parentColumnCount =
    8 + (hasCoOwnersColumn ? 1 : 0) + (hasOptions ? 1 : 0);

  const cells = [
    {
      key: `no-${index}`,
      content: <b>{index + 1}</b>,
      className: 'w-10px',
      onClick: handleViewEncumbrance,
    },

    {
      key: `shares-${index}`,
      content: formatNumber(encumbrance?.to - encumbrance?.from + 1),
      textPosition: 'right',
      onClick: handleViewEncumbrance,
    },

    {
      key: `name-${index}`,
      content: t(`ShareEncumbranceOption_${encumbranceLabel}`),
      onClick: handleViewEncumbrance,
    },

    {
      key: `from-${index}`,
      content: formatNumber(encumbrance?.from),
      textPosition: 'right',
    },

    {
      key: `to-${index}`,
      content: formatNumber(encumbrance?.to),
      textPosition: 'right',
      onClick: handleViewEncumbrance,
    },

    {
      key: `start-date-${index}`,
      content: encumbrance?.date
        ? format(new Date(encumbrance.date), dateFormat)
        : '-',
      onClick: handleViewEncumbrance,
    },

    {
      key: `end-date-${index}`,
      content: encumbrance?.endDate
        ? format(new Date(encumbrance.endDate), dateFormat)
        : '-',
      onClick: handleViewEncumbrance,
    },

    {
      key: `status-${index}`,
      content: (
        <span
          className={`badge badge-dim badge-${isActive ? 'success' : 'danger'}`}
        >
          {isActive ? t('Active') : t('Inactive')}
        </span>
      ),
      onClick: handleViewEncumbrance,
    },

    hasCoOwnersColumn
      ? {
          key: `co-owners-${index}`,
          content:
            hasCoOwners && handleExpandRow ? (
              <Button
                className="badge"
                onClick={handleExpandRow}
                variant={ButtonVariants.LIGHT}
              >
                <em
                  className={`icon ni ni-chevron-${
                    data.expandedRows.has(index) ? 'up' : 'down'
                  }`}
                />
              </Button>
            ) : (
              <span />
            ),
        }
      : null,

    hasOptions
      ? {
          key: `encumbrance-options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return (
    <>
      <TableRow cells={cells} />

      {handleExpandRow && data.expandedRows.has(index) && (
        <CoOwnersTable
          coOwners={encumbrance?.coOwners || []}
          colSpan={parentColumnCount}
        />
      )}
    </>
  );
};

export default EncumbrancesTableRow;
